import type { KeyConfig, KeyPosition } from "../../Keyboard/types";
import type {
  CommunityPreviewDeviceMeta,
  DeviceCatalogSnapshot,
  FullBoardConfigData,
  GetHigherKeyResType,
  HigherKeyMode,
  SetPerformanceParType,
} from "@eweadn-qiankun/shared-types";
import {
  isBeiying916Device,
  isHuafendaMagneticAxisEquipment,
  resolveRongyuanDecoratePreviewModeState,
  RONGYUAN_DECORATE_OFF_UI_MODE,
  RONGYUAN_LIGHT_UI_DEFAULTS,
  RONGYUAN_PERFORMANCE_DEFAULTS,
  RONGYUAN_REPORT_RATE_OPTIONS,
  supportsEs68WinLock,
} from "@eweadn-qiankun/shared-utils";
import {
  canSwapWasdArrow,
  isWasdArrowSwapped,
} from "../../../../../keyboard-management/src/utils/wasdArrowSwap";
import {
  buildPreviewKeyboardLayoutWithChangeKey,
  resolveCommunityPreviewKeyboardType,
} from "./buildPreviewKeyboardLayout";

const ADVANCED_KEY_ICON: Record<number, string> = {
  1: "dks",
  2: "mpt",
  3: "mt",
  4: "tgl",
  5: "end",
  6: "socd",
  7: "rs",
  8: "cb",
};

/** 与 SettingBefore / buildAdvancedKeyCheckDataFromListItem 所需字段一致 */
export interface AdvancedKeyAfterListItem {
  label: string;
  label2?: string;
  icon: string;
  mode: number;
  data?: {
    kcs?: number[] | number;
    row2?: number;
    col2?: number;
    socdMode?: number;
    delay?: number;
    [key: string]: unknown;
  };
  originalRow?: number;
  originalCol?: number;
  uuid?: string;
  code?: number;
  keyCode?: number;
  socdMode?: number;
  primaryRow?: number;
  primaryCol?: number;
  partnerRow?: number;
  partnerCol?: number;
}

function cellToListMeta(
  cell: KeyConfig | undefined,
  row: number,
  col: number,
  detailData?: AdvancedKeyAfterListItem["data"]
): Pick<
  AdvancedKeyAfterListItem,
  "originalRow" | "originalCol" | "uuid" | "code" | "keyCode" | "socdMode"
> {
  return {
    originalRow: cell?.originalRow ?? row,
    originalCol: cell?.originalCol ?? col,
    uuid: cell?.uuid,
    code: cell?.code,
    keyCode: cell?.code,
    socdMode: detailData?.socdMode,
  };
}

export interface LightingPreviewParams {
  mode: number;
  brightness: number;
  speed: number;
  directionIsForward: boolean;
  primaryColor: { R: number; G: number; B: number };
  selectStaticColor: number;
  lightOpen: boolean;
}

export interface PerformancePreviewValues {
  sleepMinutes: number;
  neverSleep: boolean;
  pollingRate: string;
  winLock: boolean;
  macMode: boolean;
  /** huafenda 键延迟档位文案 */
  keyDelay?: string;
  /** huafenda 键延迟档位 1–5（0 表示未选中） */
  keyDelayLevel?: number;
  /** beiying 消抖模式 i18n key */
  debounceMode?: string;
  /** beiying 消抖时间（ms） */
  debounceTimeMs?: number;
  sleepUnit: "minutes" | "seconds";
  /** 容圆：多档休眠（2.4G / 蓝牙 / 蓝牙深度） */
  rongyuanSleepSections?: RongyuanSleepPreviewSection[];
  /** 容圆：按键消抖档位 1–10 */
  rongyuanDebounce?: number;
  /** 容圆：低功耗模式 */
  rongyuanLowPowerMode?: boolean;
}

export type RongyuanSleepPreviewField =
  | "sleepTime24"
  | "sleepTimeBt"
  | "deepSleepTimeBt";

export interface RongyuanSleepPreviewSection {
  field: RongyuanSleepPreviewField;
  titleKey: string;
  minutes: number;
  neverSleep: boolean;
}

/** 社区性能预览各卡片是否展示（对齐子应用 performance 各方案页） */
export interface PerformancePreviewCardFlags {
  showSleep: boolean;
  showPolling: boolean;
  showKeyDelay: boolean;
  showDebounceMode: boolean;
  showDebounceTime: boolean;
  showWinLock: boolean;
  showMacMode: boolean;
  showRongyuanDebounce?: boolean;
  showRongyuanLowPower?: boolean;
}

/** 与子应用 huafenda/index.tsx 一致 */
const HFD_POLLING_DEVICES = new Set(["X87 V2", "X87", "X87 Neon", "X75GC"]);

/** 与子应用 huafenda/index.tsx HFD_WIRELESS_POLLING_DEVICES 一致 */
const HFD_WIRELESS_POLLING_DEVICES = new Set(["X87 V2", "X87 Neon"]);

const HFD_KEY_DELAY_DEVICES = new Set([
  "X87 V2",
  "X87",
  "X87 Neon",
  "X98 (1K)",
  "X75GC",
  "DK108",
]);

const HFD_X87_V2_POLLING_LABELS: Record<number, string> = {
  3: "1KHz",
  5: "4KHz",
  6: "8KHz",
};

const HFD_KEY_DELAY_OPTIONS = ["1档", "2档", "3档", "4档", "5档"] as const;

const DEFAULT_LIGHTING: LightingPreviewParams = {
  mode: 1,
  brightness: 50,
  speed: 50,
  directionIsForward: true,
  primaryColor: { R: 255, G: 188, B: 165 },
  selectStaticColor: 0,
  lightOpen: true,
};

/** 与 huafendaLightingBoxData / normalizeHfdLightBoxBase 关灯 mode 一致 */
export const HFD_LIGHT_BOX_UI_OFF_MODE = 5;

/** 华奋达灯箱 UI mode + 开关（对齐 LightingOperationHfd ButtonGroup 用 hfdParams.mode） */
export function resolveHuafendaLightBoxUiModeState(
  base: Record<string, unknown> | undefined
): { mode: number; lightOpen: boolean } {
  if (!base) {
    return { mode: HFD_LIGHT_BOX_UI_OFF_MODE, lightOpen: false };
  }
  const nested = base.data as Record<string, unknown> | undefined;
  const deviceBox = base.deviceLightBox as { mode?: number } | undefined;
  let mode = Number(
    nested?.mode ?? base.mode ?? deviceBox?.mode ?? HFD_LIGHT_BOX_UI_OFF_MODE
  );
  if (!Number.isFinite(mode)) {
    mode = HFD_LIGHT_BOX_UI_OFF_MODE;
  }
  /** 部分固件关灯回报 mode=0，UI 选项为 5 */
  if (mode === 0) {
    mode = HFD_LIGHT_BOX_UI_OFF_MODE;
  }

  const openRaw = nested?.open ?? base.open;
  const explicitlyClosed = openRaw === "Close" || openRaw === false;
  if (explicitlyClosed || mode === HFD_LIGHT_BOX_UI_OFF_MODE) {
    return { mode: HFD_LIGHT_BOX_UI_OFF_MODE, lightOpen: false };
  }
  return { mode, lightOpen: true };
}

/** 华丰达灯带无 decorate1Base 时的占位（避免误用背光默认值） */
const HFD_STRIP_DEFAULT_LIGHTING: LightingPreviewParams = {
  mode: HFD_LIGHT_BOX_UI_OFF_MODE,
  brightness: 0,
  speed: 0,
  directionIsForward: true,
  primaryColor: { R: 255, G: 255, B: 255 },
  selectStaticColor: 0,
  lightOpen: false,
};

const RONGYUAN_STRIP_DEFAULT_LIGHTING: LightingPreviewParams = {
  mode: RONGYUAN_DECORATE_OFF_UI_MODE,
  brightness: RONGYUAN_LIGHT_UI_DEFAULTS.luminance,
  speed: RONGYUAN_LIGHT_UI_DEFAULTS.speed,
  directionIsForward: true,
  primaryColor: { R: 0xfa, G: 0xff, B: 0xfa },
  selectStaticColor: 0,
  lightOpen: false,
};

function hfdStripLevelFromPercent(
  percent: number | undefined,
  fallback = 0
): number {
  if (typeof percent !== "number" || !Number.isFinite(percent)) {
    return fallback;
  }
  return Math.max(0, Math.min(5, Math.round(percent / 20)));
}

const DEFAULT_TRIGGER: SetPerformanceParType = {
  row: 0,
  col: 0,
  mode: 1,
  normalPress: 1.6,
  normalRelease: 1.6,
  rtFirstTouch: 0.001,
  rtPress: 0.001,
  rtRelease: 0.01,
  pressDeadStroke: 0.01,
  releaseDeadStroke: 0.01,
  calibrate: 0,
  axis: 0,
  axisV2Id: 0,
  axisRangeMax: 0,
  axisCoefficient: 0,
};

const POLLING_LABELS = [
  "8KHz",
  "4KHz",
  "2KHz",
  "1KHz",
  "500Hz",
  "250KHz",
  "125Hz",
];

const ES68_USB3_POLLING_LABELS = ["32KHz", "16KHz"];

const HFD_REPORT_RATE_LABELS: Record<number, string> = {
  0: "125Hz",
  1: "250Hz",
  2: "500Hz",
  3: "1000Hz",
  5: "4KHz",
  6: "8KHz",
};

const BEIYING_REPORT_RATE_LABELS: Record<number, string> = {
  3: "125Hz",
  2: "250Hz",
  1: "500Hz",
  0: "1000Hz",
  6: "2KHz",
  5: "4KHz",
  4: "8KHz",
};

const HFD_KEY_DELAY_LABELS: Record<number, string> = {
  1: "1档",
  2: "2档",
  3: "3档",
  4: "4档",
  5: "5档",
};

const BEIYING_DEBOUNCE_MODE_LABELS = [
  "keyboard.performance.debounceNormal",
  "keyboard.performance.debouncePre",
  "keyboard.performance.debouncePost",
  "keyboard.performance.debounceAuto",
] as const;

/** ES68 Dawn / EVO / TIANYI（洛天依）支持 USB3 高回报率选项 */
export function isEs68FamilyKeyboard(meta?: {
  keyboardType?: string;
  displayname?: string;
}): boolean {
  const kt = String(meta?.keyboardType ?? "");
  const displayname = String(meta?.displayname ?? "").trim();
  return (
    displayname === "ES68" ||
    displayname === "ES68 EVO (Full)" ||
    displayname === "ES68 EVO" ||
    displayname === "ES68 TIANYI" ||
    kt === "68EVO"
  );
}

/** 华奋达 2.4G / 蓝牙：仅无线展示休眠（对齐 performance/huafenda/index.tsx） */
function isHuafendaWirelessPreviewMeta(meta?: {
  deviceCatalog?: DeviceCatalogSnapshot;
}): boolean {
  const catalog = meta?.deviceCatalog;
  if (!catalog) return false;
  const pid = Number(catalog.productId);
  if (pid === 0xfefe) return true;
  if (pid === 0x8801) return false;
  const id = String(catalog.identifier ?? "");
  if (/dongle|65278/i.test(id)) return true;
  if (id.includes("34817")) return false;
  return false;
}

/** 各方案性能页卡片可见性 */
export function resolvePerformancePreviewCards(meta?: {
  keyboardType?: string;
  displayname?: string;
  solutionProvider?: string;
  deviceCatalog?: DeviceCatalogSnapshot;
}): PerformancePreviewCardFlags {
  const provider = meta?.solutionProvider?.toLowerCase() ?? "";
  const displayname = meta?.displayname?.trim() ?? "";

  if (provider === "huafenda") {
    const isMagnetic = isHuafendaMagneticAxisEquipment({
      solutionRovider: "huafenda",
      keyboardType: meta?.keyboardType,
      displayname: meta?.displayname,
      identifier: meta?.deviceCatalog?.identifier,
    });
    const isWireless = isHuafendaWirelessPreviewMeta(meta);
    return {
      showSleep: !isMagnetic && isWireless,
      showPolling:
        HFD_POLLING_DEVICES.has(displayname) &&
        (!isWireless || HFD_WIRELESS_POLLING_DEVICES.has(displayname)),
      showKeyDelay: !isMagnetic && HFD_KEY_DELAY_DEVICES.has(displayname),
      showDebounceMode: false,
      showDebounceTime: false,
      showWinLock: isMagnetic,
      showMacMode: false,
    };
  }

  if (provider === "rongyuan") {
    return {
      showSleep: false,
      showPolling: true,
      showKeyDelay: false,
      showDebounceMode: false,
      showDebounceTime: false,
      showWinLock: false,
      showMacMode: false,
      showRongyuanDebounce: true,
      showRongyuanLowPower: true,
    };
  }

  if (provider === "beiying" || provider === "beiyingmachine") {
    const is916 = isBeiying916Device({
      solutionRovider: "beiying",
      keyboardType: meta?.keyboardType,
      displayname: meta?.displayname,
    } as any);
    if (is916) {
      return {
        showSleep: true,
        showPolling: false,
        showKeyDelay: false,
        showDebounceMode: false,
        showDebounceTime: false,
        showWinLock: false,
        showMacMode: false,
      };
    }
    return {
      showSleep: true,
      showPolling: true,
      showKeyDelay: false,
      showDebounceMode: true,
      showDebounceTime: true,
      showWinLock: true,
      showMacMode: true,
    };
  }

  return {
    showSleep: true,
    showPolling: true,
    showKeyDelay: false,
    showDebounceMode: false,
    showDebounceTime: false,
    showWinLock: supportsEs68WinLock({
      solutionRovider: meta?.solutionProvider,
      keyboardType: meta?.keyboardType,
      displayname: meta?.displayname,
    }),
    showMacMode: false,
  };
}

export function getPerformancePollingOptions(meta?: {
  keyboardType?: string;
  displayname?: string;
  solutionProvider?: string;
}): string[] {
  const provider = meta?.solutionProvider?.toLowerCase();
  const displayname = meta?.displayname?.trim() ?? "";

  if (provider === "huafenda") {
    if (
      displayname === "X87 V2" ||
      displayname === "X87 Neon" ||
      displayname === "X75GC"
    ) {
      return ["1KHz", "4KHz", "8KHz"];
    }
    if (HFD_POLLING_DEVICES.has(displayname)) {
      return ["125Hz", "250Hz", "500Hz", "1000Hz"];
    }
    return [];
  }
  if (provider === "beiying") {
    return ["125Hz", "250Hz", "500Hz", "1000Hz", "2KHz", "4KHz", "8KHz"];
  }
  if (provider === "rongyuan") {
    return RONGYUAN_REPORT_RATE_OPTIONS.map((item) => item.label);
  }
  if (isEs68FamilyKeyboard(meta)) {
    return [...ES68_USB3_POLLING_LABELS, ...POLLING_LABELS];
  }
  return [...POLLING_LABELS];
}

export function getPerformanceKeyDelayOptions(): readonly string[] {
  return HFD_KEY_DELAY_OPTIONS;
}

/** WASD 与方向键互换预览（对齐 performance/WasdArrowSwitchSection） */
export function resolveWasdArrowPreview(
  meta: CommunityPreviewDeviceMeta,
  configData?: FullBoardConfigData,
  currentDeviceSystem = 0
): { visible: boolean; swapped: boolean } {
  const provider = meta.solutionProvider?.toLowerCase();
  if (
    provider !== "huafenda" &&
    provider !== "beiying" &&
    provider !== "beiyingmachine" &&
    provider !== "xingshan"
  ) {
    return { visible: false, swapped: false };
  }

  const keyboardType = resolveCommunityPreviewKeyboardType(meta);
  const layout = buildPreviewKeyboardLayoutWithChangeKey(
    meta,
    configData,
    currentDeviceSystem
  );
  const ctx = {
    keyboardType,
    solutionRovider: meta.solutionProvider,
    displayname: meta.displayname,
  };
  if (!canSwapWasdArrow(layout, ctx)) {
    return { visible: false, swapped: false };
  }
  return {
    visible: true,
    swapped: isWasdArrowSwapped(layout, ctx),
  };
}

function findLayoutCell(
  layout: KeyConfig[][],
  row: number,
  col: number
): KeyConfig | undefined {
  for (const layoutRow of layout) {
    for (const key of layoutRow) {
      if (key.originalRow === row && key.originalCol === col) {
        return key;
      }
    }
  }
  return undefined;
}

function findLayoutCellByLabel(
  layout: KeyConfig[][],
  label: string
): KeyConfig | undefined {
  const target = label.trim().toUpperCase();
  for (const layoutRow of layout) {
    for (const key of layoutRow) {
      if ((key.label ?? "").trim().toUpperCase() === target) {
        return key;
      }
    }
  }
  return undefined;
}

function getAdvancedKeyEntries(
  configData?: FullBoardConfigData
): [string, GetHigherKeyResType][] {
  const raw = configData?.advancedKey;
  if (!raw) return [];
  if (raw instanceof Map) return Array.from(raw.entries());
  if (Array.isArray(raw)) return raw as [string, GetHigherKeyResType][];
  return [];
}

/** 读盘/分享快照中的高级键 data（含 kcs、socdMode 等），供已配置列表展示 */
function getAdvancedKeyDetailData(
  hk: GetHigherKeyResType
): AdvancedKeyAfterListItem["data"] | undefined {
  const raw = hk as GetHigherKeyResType & {
    data?: AdvancedKeyAfterListItem["data"];
    kcs?: number[] | number;
    socdMode?: number;
    row2?: number;
    col2?: number;
    delay?: number;
  };
  if (raw.data && typeof raw.data === "object") {
    return raw.data;
  }
  if (raw.kcs != null) {
    return {
      kcs: raw.kcs,
      row2: raw.row2,
      col2: raw.col2,
      socdMode: raw.socdMode,
      delay: raw.delay,
    };
  }
  return undefined;
}

/** 从板载快照构建键盘 advancedKeyModes（uuid → 模式） */
export function buildAdvancedKeyModesFromConfig(
  layout: KeyConfig[][],
  configData?: FullBoardConfigData
): Record<string, HigherKeyMode> {
  const modes: Record<string, HigherKeyMode> = {};
  for (const [posKey, hk] of getAdvancedKeyEntries(configData)) {
    const mode = Number(hk?.mode ?? 0);
    if (!mode) continue;

    const [row, col] = posKey.split("-").map(Number);
    const cell =
      findLayoutCell(layout, row, col) ??
      findLayoutCell(layout, hk.row, hk.col);
    if (cell?.uuid) {
      modes[cell.uuid] = mode as HigherKeyMode;
    }

    const detailData = getAdvancedKeyDetailData(hk);
    if (detailData?.row2 != null && detailData?.col2 != null) {
      const cell2 = findLayoutCell(layout, detailData.row2, detailData.col2);
      if (cell2?.uuid) {
        modes[cell2.uuid] = mode as HigherKeyMode;
      }
    }
  }
  return modes;
}

/** SettingBefore 已配置列表（SOCD/RS 合并逻辑对齐 advancedKey/Home.tsx applyAdvancedKeyList） */
export function buildAdvancedKeyAfterListFromConfig(
  layout: KeyConfig[][],
  configData?: FullBoardConfigData
): AdvancedKeyAfterListItem[] {
  const entries = getAdvancedKeyEntries(configData);
  if (!entries.length) return [];

  type BuildItem = AdvancedKeyAfterListItem & {
    primaryRow?: number;
    primaryCol?: number;
    partnerRow?: number;
    partnerCol?: number;
  };

  const result: BuildItem[] = [];

  for (const [posKey, hk] of entries) {
    const mode = Number(hk?.mode ?? 0);
    if (!mode) continue;

    const [row, col] = posKey.split("-").map(Number);
    const cell =
      findLayoutCell(layout, row, col) ??
      findLayoutCell(layout, hk.row, hk.col);
    const label = cell?.label ?? posKey;
    const icon = ADVANCED_KEY_ICON[mode] ?? "mt";
    const detailData = getAdvancedKeyDetailData(hk);
    const partnerPos = detailData as
      | { row2?: number; col2?: number }
      | undefined;

    if (
      (mode === 6 || mode === 7) &&
      partnerPos?.row2 != null &&
      partnerPos?.col2 != null
    ) {
      const partnerRow = partnerPos.row2;
      const partnerCol = partnerPos.col2;

      const existingIndex = result.findIndex(
        (item) =>
          item.mode === mode &&
          item.partnerRow === row &&
          item.partnerCol === col
      );
      const reverseIndex = result.findIndex(
        (item) =>
          item.mode === mode &&
          item.primaryRow === partnerRow &&
          item.primaryCol === partnerCol
      );

      if (existingIndex !== -1) {
        result[existingIndex].label2 = label;
        if (!result[existingIndex].data && detailData) {
          result[existingIndex].data = detailData;
        }
        continue;
      }
      if (reverseIndex !== -1) {
        result[reverseIndex].label2 = label;
        if (!result[reverseIndex].data && detailData) {
          result[reverseIndex].data = detailData;
        }
        continue;
      }

      const partnerCell = findLayoutCell(layout, partnerRow, partnerCol);
      result.push({
        label,
        label2: partnerCell?.label,
        icon,
        mode,
        data: detailData,
        primaryRow: row,
        primaryCol: col,
        partnerRow,
        partnerCol,
        ...cellToListMeta(cell, row, col, detailData),
      });
      continue;
    }

    result.push({
      label,
      icon,
      mode,
      data: detailData,
      ...cellToListMeta(cell, row, col, detailData),
    });
  }

  return result.map(
    ({
      label,
      label2,
      icon,
      mode,
      data,
      originalRow,
      originalCol,
      uuid,
      code,
      keyCode,
      socdMode,
      primaryRow,
      primaryCol,
      partnerRow,
      partnerCol,
    }) => ({
      label,
      label2,
      icon,
      mode,
      data,
      originalRow,
      originalCol,
      uuid,
      code,
      keyCode,
      socdMode,
      primaryRow,
      primaryCol,
      partnerRow,
      partnerCol,
    })
  );
}

/** 灯光参数；area=Decorate1 时读 lighting.decorate1Base（灯带），否则读 lighting.base（背光） */
export function getLightingPreviewParams(
  configData?: FullBoardConfigData,
  area: "Keyboard" | "Decorate1" = "Keyboard",
  options?: { solutionProvider?: string }
): LightingPreviewParams {
  let base = (
    area === "Decorate1"
      ? configData?.lighting?.decorate1Base
      : configData?.lighting?.base
  ) as
    | (LightingPreviewParams & {
        luminance?: number;
        deviceBrightness?: number;
        deviceSpeed?: number;
        open?: string;
        direction?: string | number;
        selectStaticColor?: number;
        data?: Partial<
          LightingPreviewParams & {
            luminance?: number;
            open?: string;
            direction?: string | number;
            selectStaticColor?: number;
          }
        >;
      })
    | undefined;
  const provider = options?.solutionProvider?.toLowerCase() ?? "";
  const isHfdStrip = area === "Decorate1" && provider === "huafenda";
  const isRongyuanStrip = area === "Decorate1" && provider === "rongyuan";
  if (
    !base &&
    area === "Decorate1" &&
    provider === "xingshan" &&
    configData?.lighting?.base
  ) {
    base = configData.lighting.base as unknown as typeof base;
  }
  if (!base) {
    if (isHfdStrip) return HFD_STRIP_DEFAULT_LIGHTING;
    if (isRongyuanStrip) return RONGYUAN_STRIP_DEFAULT_LIGHTING;
    return DEFAULT_LIGHTING;
  }

  const nested = base.data;
  const hfdBoxState = isHfdStrip
    ? resolveHuafendaLightBoxUiModeState(base as Record<string, unknown>)
    : null;
  const rongyuanStripState = isRongyuanStrip
    ? resolveRongyuanDecoratePreviewModeState(base as Record<string, unknown>)
    : null;
  const mode = hfdBoxState
    ? hfdBoxState.mode
    : rongyuanStripState
      ? rongyuanStripState.mode
      : Number(nested?.mode ?? base.mode ?? DEFAULT_LIGHTING.mode);
  const deviceBrightness = base.deviceBrightness;
  const deviceSpeed = base.deviceSpeed;
  const brightness =
    typeof deviceBrightness === "number" && Number.isFinite(deviceBrightness)
      ? deviceBrightness
      : isHfdStrip
        ? hfdStripLevelFromPercent(
            Number(
              nested?.luminance ??
                nested?.brightness ??
                base.luminance ??
                (base as { brightness?: number }).brightness
            ),
            0
          )
        : Number(
            nested?.luminance ??
              nested?.brightness ??
              base.luminance ??
              (base as { brightness?: number }).brightness ??
              DEFAULT_LIGHTING.brightness
          );
  const speed =
    typeof deviceSpeed === "number" && Number.isFinite(deviceSpeed)
      ? deviceSpeed
      : isHfdStrip
        ? hfdStripLevelFromPercent(Number(nested?.speed ?? base.speed), 0)
        : Number(nested?.speed ?? base.speed ?? DEFAULT_LIGHTING.speed);
  const directionRaw = nested?.direction ?? base.direction;
  const directionIsForward =
    directionRaw === "Backward" || directionRaw === 1 || directionRaw === "1"
      ? false
      : true;
  const deviceLightBox = (
    base as {
      deviceLightBox?: { red?: number; green?: number; blue?: number };
    }
  ).deviceLightBox;
  const colorSrc = nested?.primaryColor ?? base.primaryColor;
  const colorModeData = (
    base as { colorModeData?: { R: number; G: number; B: number } }
  ).colorModeData;
  const deviceLightBoxRgb =
    deviceLightBox &&
    (deviceLightBox.red != null ||
      deviceLightBox.green != null ||
      deviceLightBox.blue != null)
      ? {
          R: Number(deviceLightBox.red) || 0,
          G: Number(deviceLightBox.green) || 0,
          B: Number(deviceLightBox.blue) || 0,
        }
      : undefined;
  const openRaw = nested?.open ?? base.open;
  const lightOpen = hfdBoxState
    ? hfdBoxState.lightOpen
    : rongyuanStripState
      ? rongyuanStripState.lightOpen
      : openRaw !== "Close" && openRaw !== false;

  return {
    mode,
    brightness,
    speed,
    directionIsForward,
    primaryColor: colorSrc ??
      colorModeData ??
      deviceLightBoxRgb ?? {
        R: 255,
        G: 188,
        B: 165,
      },
    selectStaticColor: Number(
      nested?.selectStaticColor ??
        base.selectStaticColor ??
        DEFAULT_LIGHTING.selectStaticColor
    ),
    lightOpen,
  };
}

/** 触发参数：取第一组或默认 */
export function getTriggerPreviewParams(
  configData?: FullBoardConfigData
): SetPerformanceParType {
  const group = configData?.trigger?.[0];
  if (!group) return DEFAULT_TRIGGER;

  const pos = group.keyPositions?.[0];
  return triggerGroupToParams(group, pos?.row ?? 0, pos?.col ?? 0);
}

function normalizeTriggerKeyLabel(label?: string | null): string {
  return String(label ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function buildTriggerPerformanceLookup(configData?: FullBoardConfigData): {
  byPosition: Map<string, SetPerformanceParType>;
  byKeyName: Map<string, SetPerformanceParType>;
} {
  const byPosition = new Map<string, SetPerformanceParType>();
  const byKeyName = new Map<string, SetPerformanceParType>();
  for (const group of configData?.trigger ?? []) {
    for (const pos of group.keyPositions ?? []) {
      if (pos.row == null || pos.col == null) continue;
      const params = triggerGroupToParams(group, pos.row, pos.col);
      byPosition.set(`${pos.row}-${pos.col}`, params);
      const name = normalizeTriggerKeyLabel(pos.keyName);
      if (name && !byKeyName.has(name)) {
        byKeyName.set(name, params);
      }
    }
  }
  return { byPosition, byKeyName };
}

function triggerGroupToParams(
  group: NonNullable<FullBoardConfigData["trigger"]>[number],
  row: number,
  col: number
): SetPerformanceParType {
  return {
    ...DEFAULT_TRIGGER,
    row,
    col,
    mode: group.mode ?? DEFAULT_TRIGGER.mode,
    normalPress: group.normalPress ?? DEFAULT_TRIGGER.normalPress,
    normalRelease: group.normalRelease ?? DEFAULT_TRIGGER.normalRelease,
    rtFirstTouch: group.rtFirstTouch ?? DEFAULT_TRIGGER.rtFirstTouch,
    rtPress: group.rtPress ?? DEFAULT_TRIGGER.rtPress,
    rtRelease: group.rtRelease ?? DEFAULT_TRIGGER.rtRelease,
    pressDeadStroke: group.pressDeadStroke ?? DEFAULT_TRIGGER.pressDeadStroke,
    releaseDeadStroke:
      group.releaseDeadStroke ?? DEFAULT_TRIGGER.releaseDeadStroke,
    axis: group.axis ?? DEFAULT_TRIGGER.axis,
    calibrate: group.calibrate ?? DEFAULT_TRIGGER.calibrate,
  };
}

/** 按矩阵坐标查找该键的触发参数（预览点选按键） */
export function getTriggerPreviewParamsForKey(
  configData: FullBoardConfigData | undefined,
  row: number,
  col: number,
  keyName?: string
): SetPerformanceParType {
  const { byPosition, byKeyName } = buildTriggerPerformanceLookup(configData);
  const byPos = byPosition.get(`${row}-${col}`);
  if (byPos) return byPos;

  const norm = normalizeTriggerKeyLabel(keyName);
  if (norm) {
    const byName = byKeyName.get(norm);
    if (byName) return { ...byName, row, col };
  }

  const groups = configData?.trigger ?? [];

  const explicitGroup = groups.find((item) =>
    item.keyPositions?.some((pos) => pos.row === row && pos.col === col)
  );
  if (explicitGroup) {
    return triggerGroupToParams(explicitGroup, row, col);
  }

  const globalGroup = groups.find((item) => !item.keyPositions?.length);
  if (globalGroup) {
    return triggerGroupToParams(globalGroup, row, col);
  }

  return { ...DEFAULT_TRIGGER, row, col };
}

/** 按 uuid 在 layout 中取固件矩阵 (originalRow, originalCol) */
export function resolveTriggerMatrixPosition(
  layout: KeyConfig[][],
  uuid: string,
  fallback?: KeyPosition
): { row: number; col: number } {
  for (const layoutRow of layout) {
    for (const key of layoutRow) {
      if (key.uuid !== uuid) continue;
      if (key.originalCol === undefined || key.originalCol < 0) break;
      return {
        row: key.originalRow ?? fallback?.row ?? 0,
        col: key.originalCol ?? fallback?.col ?? 0,
      };
    }
  }
  if (fallback) {
    return { row: fallback.row, col: fallback.col };
  }
  return { row: 0, col: 0 };
}

/** 点选时补全 selectData（Keyboard 对 ES68 等可能只给 UI 行号） */
export function enrichTriggerSelectData(
  layout: KeyConfig[][],
  selectedKeys: string[],
  selectData: Record<string, KeyPosition>
): Record<string, KeyPosition> {
  const enriched = { ...selectData };
  for (const uuid of selectedKeys) {
    if (enriched[uuid]) {
      const matrix = resolveTriggerMatrixPosition(layout, uuid, enriched[uuid]);
      enriched[uuid] = {
        ...enriched[uuid],
        row: matrix.row,
        col: matrix.col,
      };
      continue;
    }
    for (const row of layout) {
      for (const key of row) {
        if (
          key.uuid !== uuid ||
          !key.uuid ||
          key.code === "__gap__" ||
          key.originalCol === undefined ||
          key.originalCol < 0
        ) {
          continue;
        }
        enriched[uuid] = {
          row: key.originalRow ?? 0,
          col: key.originalCol,
          code: key.code,
          label: key.label || "",
          uuid: key.uuid,
        };
        break;
      }
    }
  }
  return enriched;
}

/** 预览初始选中：定位 trigger 配置里第一个按键并在键盘上高亮 */
export function resolveTriggerPreviewInitialSelection(
  layout: KeyConfig[][],
  configData?: FullBoardConfigData
): { selectedKeys: string[]; selectData: Record<string, KeyPosition> } {
  const groups = configData?.trigger ?? [];
  const targetPos = groups
    .flatMap((group) => group.keyPositions ?? [])
    .find((pos) => pos.row != null && pos.col != null);

  if (!targetPos) {
    return { selectedKeys: [], selectData: {} };
  }

  const matchByMatrix = (): KeyConfig | undefined => {
    for (let rowIndex = 0; rowIndex < layout.length; rowIndex++) {
      for (const key of layout[rowIndex]) {
        if (
          !key.uuid ||
          key.code === "__gap__" ||
          key.originalCol === undefined ||
          key.originalCol < 0
        ) {
          continue;
        }
        const row = key.originalRow ?? rowIndex;
        const col = key.originalCol;
        if (row === targetPos.row && col === targetPos.col) {
          return key;
        }
      }
    }
    return undefined;
  };

  let key = matchByMatrix();
  if (!key && targetPos.keyName) {
    key = findLayoutCellByLabel(layout, targetPos.keyName) ?? undefined;
  }

  if (!key?.uuid) {
    return { selectedKeys: [], selectData: {} };
  }

  const row = key.originalRow ?? targetPos.row;
  const col = key.originalCol ?? targetPos.col;

  return {
    selectedKeys: [key.uuid],
    selectData: {
      [key.uuid]: {
        row,
        col,
        code: key.code,
        label: key.label || "",
        uuid: key.uuid,
      },
    },
  };
}

export function hasTriggerPreviewConfig(
  configData?: FullBoardConfigData
): boolean {
  return (configData?.trigger?.length ?? 0) > 0;
}

/**
 * 为 Keyboard 的 teampresetData 注入每键 performance（对齐子应用 customize 页 newLayout）。
 * 使用完整矩阵 layout，Keyboard 内部 slice 后会通过 resolveLayoutDataRowIndex 对齐行号。
 */
export function buildTriggerPreviewLayout(
  layout: KeyConfig[][],
  configData?: FullBoardConfigData
): KeyConfig[][] {
  if (!hasTriggerPreviewConfig(configData)) {
    return layout;
  }

  const { byPosition, byKeyName } = buildTriggerPerformanceLookup(configData);

  return layout.map((row, rowIndex) =>
    row.map((key, colIndex) => {
      if (
        !key.uuid ||
        key.code === "__gap__" ||
        key.originalCol === undefined ||
        key.originalCol < 0
      ) {
        return key;
      }

      const matrixRow = key.originalRow ?? rowIndex;
      const matrixCol = key.originalCol ?? colIndex;
      const performance =
        byPosition.get(`${matrixRow}-${matrixCol}`) ??
        byKeyName.get(normalizeTriggerKeyLabel(key.label)) ??
        getTriggerPreviewParamsForKey(
          configData,
          matrixRow,
          matrixCol,
          key.label
        );

      return { ...key, performance };
    })
  );
}

/** 改键预览逻辑见 changeKeyPreviewUtils.ts */
export {
  applyChangeKeyRemaps,
  getChangeKeyLayerOptions,
  getChangeKeySelectedUuids,
  type ChangeKeyLayerOption,
} from "./changeKeyPreviewUtils";

export { DEFAULT_LIGHTING, DEFAULT_TRIGGER, POLLING_LABELS };
export function getPerformancePreviewValues(
  configData?: FullBoardConfigData,
  meta?: {
    keyboardType?: string;
    displayname?: string;
    solutionProvider?: string;
  }
): PerformancePreviewValues {
  const perf = (configData?.performance ?? {}) as Record<string, unknown>;
  const provider = meta?.solutionProvider?.toLowerCase();

  if (provider === "huafenda") {
    const sleepRaw = perf.sleepTime ?? perf.sleepMinutes ?? perf.sleep;
    const sleepMinutes =
      typeof sleepRaw === "number"
        ? sleepRaw > 120
          ? Math.round(sleepRaw / 60)
          : sleepRaw
        : 50;
    const neverSleep = sleepRaw === 0;
    const rateIndex = Number(perf.reportRate ?? 3);
    const keyDelayIndex = Number(perf.keyDelay ?? 0);
    const displayname = meta?.displayname?.trim() ?? "";
    const pollingRate =
      displayname === "X87 V2" ||
      displayname === "X87 Neon" ||
      displayname === "X75GC"
        ? (HFD_X87_V2_POLLING_LABELS[rateIndex] ?? "1KHz")
        : (HFD_REPORT_RATE_LABELS[rateIndex] ?? "1000Hz");
    return {
      sleepMinutes: neverSleep ? 0 : sleepMinutes,
      neverSleep,
      pollingRate,
      winLock: false,
      macMode: false,
      keyDelay:
        keyDelayIndex > 0
          ? (HFD_KEY_DELAY_LABELS[keyDelayIndex] ??
            HFD_KEY_DELAY_OPTIONS[keyDelayIndex - 1])
          : undefined,
      keyDelayLevel: keyDelayIndex,
      sleepUnit: "minutes",
    };
  }

  if (provider === "rongyuan") {
    const readSleepMinutes = (value: unknown, fallback: number): number => {
      if (value === undefined || value === null) return fallback;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };
    const sleepTime24 = readSleepMinutes(
      perf.sleepTime24 ?? perf.sleepTime,
      RONGYUAN_PERFORMANCE_DEFAULTS.sleepTime24Minutes
    );
    const sleepTimeBt = readSleepMinutes(
      perf.sleepTimeBt,
      RONGYUAN_PERFORMANCE_DEFAULTS.sleepTimeBtMinutes
    );
    const deepSleepTimeBt = readSleepMinutes(
      perf.deepSleepTimeBt,
      RONGYUAN_PERFORMANCE_DEFAULTS.deepSleepTimeBtMinutes
    );
    const pollingHz = Number(
      perf.pollingRate ?? RONGYUAN_PERFORMANCE_DEFAULTS.pollingRateHz
    );
    const pollingLabel =
      RONGYUAN_REPORT_RATE_OPTIONS.find((item) => item.hz === pollingHz)
        ?.label ?? `${pollingHz}Hz`;
    return {
      sleepMinutes: sleepTime24,
      neverSleep: sleepTime24 === 0,
      pollingRate: pollingLabel,
      winLock: false,
      macMode: false,
      sleepUnit: "minutes",
      rongyuanSleepSections: [
        {
          field: "sleepTime24",
          titleKey: "keyboard.performance.rongyuan.sleep24Title",
          minutes: sleepTime24,
          neverSleep: sleepTime24 === 0,
        },
        {
          field: "sleepTimeBt",
          titleKey: "keyboard.performance.rongyuan.sleepBtTitle",
          minutes: sleepTimeBt,
          neverSleep: sleepTimeBt === 0,
        },
        {
          field: "deepSleepTimeBt",
          titleKey: "keyboard.performance.rongyuan.deepSleepBtTitle",
          minutes: deepSleepTimeBt,
          neverSleep: deepSleepTimeBt === 0,
        },
      ],
      rongyuanDebounce: Number(
        perf.debounce ?? RONGYUAN_PERFORMANCE_DEFAULTS.debounce
      ),
      rongyuanLowPowerMode: Boolean(
        perf.lowPowerMode ?? RONGYUAN_PERFORMANCE_DEFAULTS.lowPowerMode
      ),
    };
  }

  if (provider === "beiying" || provider === "beiyingmachine") {
    const is916 = isBeiying916Device({
      solutionRovider: "beiying",
      keyboardType: meta?.keyboardType,
      displayname: meta?.displayname,
    } as any);
    const sleepRaw = Number(
      perf.sleepTime ?? perf.sleepMinutes ?? (is916 ? 30 : 180)
    );
    if (is916) {
      const sleepSeconds = Math.max(0, Math.trunc(sleepRaw));
      return {
        sleepMinutes: sleepSeconds,
        neverSleep: sleepSeconds === 0,
        pollingRate: "",
        winLock: false,
        macMode: false,
        sleepUnit: "seconds",
      };
    }
    const sleepSeconds = sleepRaw > 120 ? sleepRaw : sleepRaw * 60;
    const neverSleep = sleepSeconds === 0;
    const rateIndex = Number(perf.reportRate ?? 0);
    const debounceModeIndex = Number(perf.debounceMode ?? 0);
    const debounceTimeMs = Math.max(
      1,
      Math.round(Number(perf.debounceTime ?? 10000) / 1000)
    );
    return {
      sleepMinutes: neverSleep ? 0 : Math.round(sleepSeconds / 60),
      neverSleep,
      pollingRate: BEIYING_REPORT_RATE_LABELS[rateIndex] ?? "1000Hz",
      winLock: perf.gameMode === true || perf.gameMode === 1,
      macMode: Number(perf.osMode ?? 0) === 1,
      debounceMode: BEIYING_DEBOUNCE_MODE_LABELS[debounceModeIndex],
      debounceTimeMs,
      sleepUnit: "minutes",
    };
  }

  const sleepRaw = perf.sleepMinutes ?? perf.sleepTime ?? perf.sleep;
  const sleepMinutes =
    typeof sleepRaw === "number"
      ? sleepRaw > 120
        ? Math.round(sleepRaw / 60)
        : sleepRaw
      : 50;
  const neverSleep =
    perf.neverSleep != null ? Boolean(perf.neverSleep) : sleepRaw === 0;
  const pollingLabelRaw =
    typeof perf.pollingRateLabel === "string"
      ? perf.pollingRateLabel
      : undefined;
  const pollingIndex = Number(
    perf.pollingRate ?? perf.reportRate ?? perf.polling ?? 5
  );
  const pollingOptions = getPerformancePollingOptions(meta);
  const pollingRate =
    pollingLabelRaw?.replace(/^R/i, "") ??
    pollingOptions[pollingIndex] ??
    POLLING_LABELS[pollingIndex] ??
    "250KHz";

  return {
    sleepMinutes: neverSleep ? 0 : sleepMinutes,
    neverSleep,
    pollingRate,
    winLock: Boolean(perf.winLock ?? perf.winKeyLock ?? false),
    macMode: Boolean(perf.macMode ?? perf.macSystem ?? false),
    sleepUnit: "minutes",
  };
}

export function findKeyPositionByLabel(
  layout: KeyConfig[][],
  label: string
): { row: number; col: number } | null {
  const cell = findLayoutCellByLabel(layout, label);
  if (!cell) return null;
  return { row: cell.originalRow, col: cell.originalCol };
}

/** 性能页预览值 */
